import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { APIProvider } from '@vis.gl/react-google-maps';
import logo from './logo_colored_transp_no_text.png';
import Navbar from './components/Navbar';
import PrivacyPolicy from './PrivacyPolicy';
import RegisterForm from './components/RegisterForm';
import AccountDeletionPage from './components/AccountDeletionPage';
import KapcsolatOldal from './components/Contacts';
import CreateCompetitionPage from './components/CreateCompetitionPage';
import EventsListPage from './components/EventsListPage';
import FullViewCompetition from './components/FullViewCompetition';
import BottomNavbar from './components/BottomNavbar';
import QrScannerWeb from './components/competitors/QrScannerWeb';
import CompetitionScoringWeb from './components/competitors/CompetitionScoringWeb';
import MyWorkouts from './components/competitors/MyWorkouts';
import WorkoutDetails from './components/competitors/WorkoutDetails';
import ResultsListPage from './components/competitors/ResultsListPage';
import ClosedCompetitionLeaderboard from './components/competitors/ClosedCompetitionLeaderboard';
import CompetitorResultView from './components/competitors/CompetitorResultView';
import PasswordResetPage from './components/PasswordResetPage';
import UpdatePrompt from './components/common/UpdatePrompt';
import ScrollToTop from './components/common/ScrollToTop';

import HomePage from './components/HomePage';

import ClubDashboard from './components/clubs/ClubDashboard';
import EditCompetitionPage from './components/clubs/EditCompetitionPage'; 
import CompetitionOffice from './components/clubs/CompetitionOffice';
import FeedbackPage from './components/clubs/FeedbackPage';
import Tournaments from './components/clubs/Tournaments';
import CreateTournament from './components/clubs/CreateTournament';
import EditTournament from './components/clubs/EditTournament';
import ChampionshipSummary from './components/clubs/TournamentSummary';

import CompetitorDashboard from './components/competitors/CompetitorDashboard.js';
import EditProfile from './components/competitors/EditProfile';

// ÚJ OLDALAK
import ClubSearchPage from './components/clubs/ClubSearchPage';
import ClubDetailsPage from './components/clubs/ClubDetailsPage';
import SocialFeed from './components/SocialFeed';
import Friendlist from './components/Friendlist';
import CreateWorkout from './components/competitors/CreateWorkout';
import Workout from './components/competitors/Workout';
import PublicCompetitionsApi from './components/PublicCompetitionsApi';

import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

function CompetitorRouteWrapper() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setCurrentUser({
              ...user,
              displayName: `${data.lastName || ''} ${data.firstName || ''}`.trim() || user.displayName
            });
          } else {
            setCurrentUser(user);
          }
        } catch (e) {
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
      }
      setLoadingUser(false);
    });
    return () => unsubscribe();
  }, []);

  if (loadingUser) {
    return <div style={{ textAlign: 'center', padding: '50px', fontFamily: 'Arial' }}>Felhasználó ellenőrzése... 🏹</div>;
  }

  if (!currentUser) {
    return <div style={{ textAlign: 'center', padding: '50px', fontFamily: 'Arial' }}>Kérlek jelentkezz be a profil eléréséhez!</div>;
  }

  return <CompetitorDashboard currentUser={currentUser} onLogout={() => auth.signOut()} />;
}


function App() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const lang = navigator.language || navigator.userLanguage || '';
    if (lang && !lang.toLowerCase().startsWith('hu')) {
      document.title = "ArcheryNexus - The entire archery world in your pocket!";
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', 'ArcheryNexus – The entire archery world in your pocket!');
      }
    }

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // AUTOMATA HELYREÁLLÍTÁS (Auto-Recovery): Ha a Firestore users/{uid} megszakadás miatt hiányzik
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (!userDocSnap.exists()) {
            const storedDataStr = localStorage.getItem(`pending_user_data_${user.uid}`) || localStorage.getItem('pending_user_data_latest');
            if (storedDataStr) {
              try {
                const storedData = JSON.parse(storedDataStr);
                if (storedData.uid === user.uid && storedData.role === 'user') {
                  const recoveryData = {
                    ...storedData,
                    createdAt: serverTimestamp()
                  };
                  await setDoc(userDocRef, recoveryData);
                  console.log('✅ Automata helyreállítás: users/{uid} sikeresen pótolva a helyi mentésből!');
                  localStorage.removeItem(`pending_user_data_${user.uid}`);
                  localStorage.removeItem('pending_user_data_latest');
                }
              } catch (recErr) {
                console.error('Helyreállítási hiba:', recErr);
              }
            }
          }
        } catch (checkErr) {
          console.error('Auth állapot ellenőrzési hiba:', checkErr);
        }
      }

      setCurrentUser(user);
      setAuthLoading(false);
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      unsubscribe();
    };
  }, []);

  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        backgroundColor: '#22252a',
        fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        color: '#ffffff',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 99999
      }}>
        <div style={{
          position: 'relative',
          width: '110px',
          height: '110px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <img
            src={logo}
            alt="ArcheryNexus"
            style={{
              width: '100px',
              height: '100px',
              objectFit: 'contain',
              filter: 'drop-shadow(0 0 22px rgba(179,0,0,0.75))'
            }}
          />
        </div>

        <h1 style={{
          margin: '0 0 6px 0',
          fontSize: '2.2rem',
          fontWeight: '800',
          letterSpacing: '1.5px',
          color: '#ffffff'
        }}>
          Archery<span style={{ color: '#b30000' }}>Nexus</span>
        </h1>

        <div style={{
          fontSize: '0.85rem',
          color: '#aaaaaa',
          letterSpacing: '2px',
          textTransform: 'uppercase',
          marginBottom: '28px'
        }}>
          Shooting Profile &amp; Community
        </div>

        <div style={{
          width: '180px',
          height: '4px',
          backgroundColor: '#333842',
          borderRadius: '4px',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.4)'
        }}>
          <div style={{
            position: 'absolute',
            height: '100%',
            background: 'linear-gradient(90deg, #b30000, #ff4d4d)',
            borderRadius: '4px',
            animation: 'webSplashPulse 1.5s cubic-bezier(0.4, 0, 0.2, 1) infinite'
          }} />
        </div>

        <style>{`
          @keyframes webSplashPulse {
            0% { left: -40%; width: 35%; }
            50% { left: 30%; width: 50%; }
            100% { left: 105%; width: 35%; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <APIProvider apiKey={'AIzaSyCUQ1dNjylL6QZRs6nWbVyfKRSarbEXgKI'} libraries={['places', 'marker']}>
      <div className="force-light-theme" style={{ minHeight: '100vh' }}>
        <Router>
          <ScrollToTop />
          <Navbar />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/adatvedelem" element={<PrivacyPolicy />} />
            <Route path="/regisztracio" element={<RegisterForm />} />
            <Route path="/fioktorles" element={<AccountDeletionPage />} />
            <Route path="/kapcsolat" element={<KapcsolatOldal />} />
            <Route path="/verseny-letrehozas" element={<CreateCompetitionPage />} />
            <Route path="/verseny-szerkesztes/:id" element={<EditCompetitionPage />} />
            <Route path="/versenyiroda/:id" element={<CompetitionOffice />} />
            <Route path="/kupasorozat-letrehozas" element={<CreateTournament />} />
            <Route path="/kupasorozat-szerkesztes/:id" element={<EditTournament />} />
            <Route path="/kupasorozat-osszesites/:id" element={<ChampionshipSummary />} />
            <Route path="/versenyek" element={<EventsListPage />} />
            <Route path="/verseny/:id" element={<FullViewCompetition />} />
            <Route path="/api/competitions.json" element={<PublicCompetitionsApi />} />
            <Route path="/egyesulet-dashboard" element={<ClubDashboard />} />
            <Route path="/visszajelzes" element={<FeedbackPage />} />
            <Route path="/profil" element={<CompetitorRouteWrapper />} />
            <Route path="/profil-szerkesztes" element={<EditProfile />} />
            <Route path="/edzeseim" element={<MyWorkouts />} />
            <Route path="/createworkout" element={<CreateWorkout />} />
            <Route path="/workout/:id" element={<Workout />} />
            <Route path="/edzes/:id" element={<WorkoutDetails />} />
            <Route path="/eredmenyek" element={<ResultsListPage />} />
            <Route path="/eredmeny/:id" element={<ClosedCompetitionLeaderboard />} />
            <Route path="/eredmeny/:compId/resztvevo/:pId" element={<CompetitorResultView />} />
            <Route path="/jelszovisszaallitas" element={<PasswordResetPage />} />
            <Route path="/qr-scanner" element={<QrScannerWeb />} />
            <Route path="/competition-scoring-web" element={<CompetitionScoringWeb />} />

            {/* ÚJ EGYESÜLETI ÚTVONALAK */}
            <Route path="/egyesuletek" element={<ClubSearchPage />} />
            <Route path="/egyesulet/:id" element={<ClubDetailsPage />} />
            <Route path="/kozosseg" element={<SocialFeed />} />
            <Route path="/baratok" element={<Friendlist />} />

            <Route
              path="/nevezes/:eventId"
              element={<div>Nevezési oldal (Fejlesztés alatt)</div>}
            />
          </Routes>

          {isMobile && currentUser && <BottomNavbar />}
          <UpdatePrompt />
        </Router>
      </div>
    </APIProvider>
  );
}

export default App;
